import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useWebSocket, useUser } from './WebSocketContext';
import {
  Typography,
  Box,
  Tooltip,
  IconButton,
  Paper,
  Alert,
  Fade,
  Grow,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import SettingFolders from './SettingFoldersConnector';
import GenericConnector from './GenericConnector';
import ConnectorCreatorDialog from './ConnectorCreatorDialog';

const SettingDatasources = ({ caseid, hideHeader = false, autoOpenConnector, onConnectorOpened }) => {
  const { t } = useTranslation();
  const { isReadOnly } = useUser();
  const { serverInfo, sendMessage, subscribeToMessages } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatorDialogOpen, setIsCreatorDialogOpen] = useState(false);
  const [createdConnectorToOpen, setCreatedConnectorToOpen] = useState('');
  const [connectorProfiles, setConnectorProfiles] = useState({});
  const [suggestedConnectorTypes, setSuggestedConnectorTypes] = useState([]);
  const [otherSectionExpanded, setOtherSectionExpanded] = useState(false);
  const isWindowsServer = serverInfo?.isWindows === true
    || serverInfo?.osType === 'win32'
    || serverInfo?.platform === 'Windows';
  const pluginConnectorsList = Array.isArray(serverInfo?.connectorPlugins)
    ? serverInfo.connectorPlugins
    : [];
  const hasContainerizedPluginConnectors = pluginConnectorsList.some((plugin) => Boolean(plugin?.isContainerized));
  const pluginRuntimeEnabled = Boolean(serverInfo?.pluginRuntime?.enabled);
  const showPluginRuntimeWarning = hasContainerizedPluginConnectors && !pluginRuntimeEnabled;
  const normalizeConnectorType = useCallback(
    (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, ''),
    []
  );
  const allowedConnectorSet = useMemo(() => {
    const raw = serverInfo?.allowedConnectors || serverInfo?.allowed_connectors || [];
    if (!Array.isArray(raw)) {
      return new Set();
    }
    const normalized = raw
      .map((item) => normalizeConnectorType(item))
      .filter(Boolean);
    return new Set(normalized);
  }, [serverInfo, normalizeConnectorType]);
  const disabledConnectorSet = useMemo(() => {
    const raw = serverInfo?.disabledConnectors || serverInfo?.disabled_connectors || [];
    if (!Array.isArray(raw)) {
      return new Set();
    }
    const normalized = raw
      .map((item) => normalizeConnectorType(item))
      .filter(Boolean);
    return new Set(normalized);
  }, [serverInfo, normalizeConnectorType]);

  useEffect(() => {
    if (typeof sendMessage !== 'function') {
      return;
    }
    sendMessage({ command: 'get_server_info', data: {} });
  }, [sendMessage]);

  useEffect(() => {
    if (typeof subscribeToMessages !== 'function') {
      return undefined;
    }
    const unsubscribe = subscribeToMessages(['listGenericDatasources'], (parsedMessage) => {
      const payload = parsedMessage?.data || {};
      const connectorType = String(payload?.type || '').trim();
      const normalizedType = normalizeConnectorType(connectorType);
      if (!normalizedType) {
        return;
      }
      const datasources = Array.isArray(payload?.datasources) ? payload.datasources : [];
      const nextProfileState = {
        hasProfile: datasources.length > 0,
        hasEnabledProfile: datasources.some((entry) => Number(entry?.enable) === 1),
      };
      setConnectorProfiles((prev) => {
        const current = prev[normalizedType];
        if (
          current &&
          current.hasProfile === nextProfileState.hasProfile &&
          current.hasEnabledProfile === nextProfileState.hasEnabledProfile
        ) {
          return prev;
        }
        return {
          ...prev,
          [normalizedType]: nextProfileState,
        };
      });
    });
    return unsubscribe;
  }, [subscribeToMessages, normalizeConnectorType]);

  useEffect(() => {
    if (typeof subscribeToMessages !== 'function') {
      return undefined;
    }
    const unsubscribe = subscribeToMessages(['getDatasourceSuggestions'], (parsedMessage) => {
      const payload = parsedMessage?.data || {};
      const caseId = Number(payload?.caseid);
      if (!Number.isFinite(caseId) || Number(caseid) !== caseId) {
        return;
      }
      const rawSuggestedTypes = Array.isArray(payload?.suggestedTypes) ? payload.suggestedTypes : [];
      setSuggestedConnectorTypes(rawSuggestedTypes);
    });
    return unsubscribe;
  }, [subscribeToMessages, caseid]);

  useEffect(() => {
    // Avoid stale profile highlights when switching cases.
    setConnectorProfiles({});
    setCreatedConnectorToOpen('');
    setSuggestedConnectorTypes([]);
    setOtherSectionExpanded(false);
  }, [caseid]);

  // Trigger animation when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const pluginConnectors = useMemo(() => {
    const rawPlugins = serverInfo?.connectorPlugins;
    if (!Array.isArray(rawPlugins)) {
      return [];
    }

    return rawPlugins
      .map((plugin) => {
        const pluginType = String(plugin?.type || '').trim();
        const pluginName = String(plugin?.name || pluginType).trim();
        const pluginGuideUrl = String(plugin?.guideUrl || '').trim();
        const pluginOAuth = plugin?.oauth && typeof plugin.oauth === 'object'
          ? plugin.oauth
          : (plugin?.oauthConfig && typeof plugin.oauthConfig === 'object'
            ? plugin.oauthConfig
            : (plugin?.oauth_config && typeof plugin.oauth_config === 'object' ? plugin.oauth_config : null));
        const pluginMemory = plugin?.memory && typeof plugin.memory === 'object'
          ? plugin.memory
          : (plugin?.pluginMemory && typeof plugin.pluginMemory === 'object'
            ? plugin.pluginMemory
            : (plugin?.plugin_memory && typeof plugin.plugin_memory === 'object' ? plugin.plugin_memory : null));
        const loadFailed = Boolean(plugin?.loadFailed);
        const loadError = String(plugin?.loadError || plugin?.loadErrorMessage || plugin?.error || '');
        const isGeneratedPlugin = plugin?.isAIGenerated === true
          || /^auto_/i.test(pluginType)
          || /^auto_/i.test(pluginName)
          || /_auto_/i.test(pluginType)
          || /generated by ai/i.test(String(plugin?.description || ''));
        if (!pluginType || !pluginName) {
          return null;
        }
        return {
          Component: GenericConnector,
          props: {
            caseid,
            type: pluginType,
            title: pluginName,
            useImageIcon: true,
            guideUrl: pluginGuideUrl,
            aiGenerated: isGeneratedPlugin,
            pluginOAuth,
            pluginMemory,
            loadFailed,
            loadError,
          },
          title: pluginName,
          isPluginConnector: true,
        };
      })
      .filter(Boolean);
  }, [serverInfo, caseid]);

  // Define all connectors in an array for easier animation mapping
  const staticConnectors = [
    ...(isWindowsServer ? [
      { Component: GenericConnector, props: { caseid, type: "outlooklocal", title: "Outlook", useImageIcon: true, sx: { color: '#5757b0' } }, title: "Outlook" },
    ] : []),
    { Component: GenericConnector, props: { caseid, type: "outlookapi", title: "Outlook API", useImageIcon: true, sx: { color: '#5757b0' } }, title: "Outlook API" },
    { Component: SettingFolders, props: { caseid }, title: "Folders" },
    { Component: GenericConnector, props: { caseid, type: "API", title: "Generic API", useImageIcon: true }, title: "Generic API" },
    { Component: GenericConnector, props: { caseid, type: "Dropbox", title: "Dropbox", useImageIcon: true }, title: "Dropbox" },
    { Component: GenericConnector, props: { caseid, type: "OneDrive", title: "OneDrive", useImageIcon: true }, title: "OneDrive" },
    { Component: GenericConnector, props: { caseid, type: "GoogleDrive", title: "Google Drive", useImageIcon: true }, title: "Google Drive" },
    { Component: GenericConnector, props: { caseid, type: "Excel", title: "Excel", useImageIcon: true }, title: "Excel" },
    { Component: GenericConnector, props: { caseid, type: "CSV", title: "CSV", useImageIcon: true }, title: "CSV" },
    { Component: GenericConnector, props: { caseid, type: "Gmail", title: "Google Gmail", useImageIcon: true }, title: "Gmail" },
    { Component: GenericConnector, props: { caseid, type: "Gnews", title: "GNews.io", useImageIcon: true }, title: "GNews" },
    { Component: GenericConnector, props: { caseid, type: "MCP", title: "MCP Connector", useImageIcon: true }, title: "MCP" },
    { Component: GenericConnector, props: { caseid, type: "Mediastack", title: "Mediastack", useImageIcon: true }, title: "Mediastack" },
    { Component: GenericConnector, props: { caseid, type: "NewsAPIAI", title: "NewsAPI.ai", useImageIcon: true }, title: "NewsAPI.ai" },
    { Component: GenericConnector, props: { caseid, type: "NewsAPI", title: "NewsAPI.api", useImageIcon: true }, title: "NewsAPI" },
    { Component: GenericConnector, props: { caseid, type: "Salesforce", title: "Salesforce", useImageIcon: true }, title: "Salesforce" },
    { Component: GenericConnector, props: { caseid, type: "Slack", title: "Slack", useImageIcon: true }, title: "Slack" },
    { Component: GenericConnector, props: { caseid, type: "Whatsapp", title: "Whatsapp", useImageIcon: true }, title: "Whatsapp" },
    { Component: GenericConnector, props: { caseid, type: "Airtable", title: "Airtable", useImageIcon: true }, title: "Airtable" },
    { Component: GenericConnector, props: { caseid, type: "Notion", title: "Notion", useImageIcon: true }, title: "Notion" },
    { Component: GenericConnector, props: { caseid, type: "Test", title: "Test Connector", useImageIcon: true }, title: "Test" },
    { Component: GenericConnector, props: { caseid, type: "PluginExamplePolling", title: "Plugin Example Polling", useImageIcon: true }, title: "Plugin Example Polling" },
    { Component: GenericConnector, props: { caseid, type: "PythonScript", title: "Python Script", useImageIcon: true }, title: "Python Script" },
    { Component: GenericConnector, props: { caseid, type: "WebScraper", title: "Web Scraper", useImageIcon: true }, title: "Web Scraper" },
  ];

  const allConnectors = useMemo(() => {
    const existingTypes = new Set(
      staticConnectors
        .map((connector) => normalizeConnectorType(connector?.props?.type || connector?.title || ''))
        .filter(Boolean)
    );
    const dedupedPlugins = pluginConnectors.filter((connector) => {
      const normalized = normalizeConnectorType(connector?.props?.type || connector?.title || '');
      return normalized && !existingTypes.has(normalized);
    });
    return [...staticConnectors, ...dedupedPlugins];
  }, [staticConnectors, pluginConnectors]);

  const visibleConnectors = allowedConnectorSet.size > 0
    ? allConnectors.filter((connector) => {
      const connectorType = connector?.props?.type || connector?.title || '';
      const normalized = normalizeConnectorType(connectorType);
      if (connector?.isPluginConnector) {
        return !disabledConnectorSet.has(normalized);
      }
      return allowedConnectorSet.has(normalized) && !disabledConnectorSet.has(normalized);
    })
    : allConnectors.filter((connector) => {
      const connectorType = connector?.props?.type || connector?.title || '';
      const normalized = normalizeConnectorType(connectorType);
      return !disabledConnectorSet.has(normalized);
    });

  const sortConnectorsByProfile = useCallback((connectors) => {
    const connectorsCopy = [...(Array.isArray(connectors) ? connectors : visibleConnectors)];
    connectorsCopy.sort((left, right) => {
      const leftType = normalizeConnectorType(left?.props?.type || left?.title || '');
      const rightType = normalizeConnectorType(right?.props?.type || right?.title || '');
      const leftProfile = connectorProfiles[leftType] || {};
      const rightProfile = connectorProfiles[rightType] || {};

      const leftIsEnabledFolders = leftType === 'folders' && Boolean(leftProfile.hasEnabledProfile);
      const rightIsEnabledFolders = rightType === 'folders' && Boolean(rightProfile.hasEnabledProfile);
      if (leftIsEnabledFolders !== rightIsEnabledFolders) {
        return rightIsEnabledFolders ? 1 : -1;
      }

      const leftHasProfile = leftProfile.hasProfile ? 1 : 0;
      const rightHasProfile = rightProfile.hasProfile ? 1 : 0;
      if (leftHasProfile !== rightHasProfile) {
        return rightHasProfile - leftHasProfile;
      }

      const leftEnabled = leftProfile.hasEnabledProfile ? 1 : 0;
      const rightEnabled = rightProfile.hasEnabledProfile ? 1 : 0;
      if (leftEnabled !== rightEnabled) {
        return rightEnabled - leftEnabled;
      }

      return String(left?.title || '').localeCompare(String(right?.title || ''));
    });
    return connectorsCopy;
  }, [visibleConnectors, connectorProfiles, normalizeConnectorType]);

  const prioritizedConnectors = useMemo(
    () => sortConnectorsByProfile(visibleConnectors),
    [visibleConnectors, sortConnectorsByProfile]
  );

  const suggestedConnectorSet = useMemo(() => {
    const normalized = suggestedConnectorTypes
      .map((item) => normalizeConnectorType(item))
      .filter(Boolean);
    return new Set(normalized);
  }, [suggestedConnectorTypes, normalizeConnectorType]);

  const hasExplicitSuggestions = suggestedConnectorSet.size > 0;

  const suggestedConnectors = useMemo(() => {
    if (!hasExplicitSuggestions) {
      return [];
    }

    const connectorMap = new Map();
    prioritizedConnectors.forEach((connector) => {
      const normalized = normalizeConnectorType(connector?.props?.type || connector?.title || '');
      if (normalized) {
        connectorMap.set(normalized, connector);
      }
    });

    const orderedSuggested = [];
    suggestedConnectorTypes.forEach((connectorType) => {
      const normalized = normalizeConnectorType(connectorType);
      if (!normalized) {
        return;
      }
      const connector = connectorMap.get(normalized);
      if (connector) {
        orderedSuggested.push(connector);
      }
    });
    return orderedSuggested;
  }, [hasExplicitSuggestions, prioritizedConnectors, suggestedConnectorTypes, normalizeConnectorType]);

  const otherConnectors = useMemo(() => {
    if (!hasExplicitSuggestions) {
      return [];
    }
    return prioritizedConnectors.filter((connector) => {
      const normalized = normalizeConnectorType(connector?.props?.type || connector?.title || '');
      return normalized && !suggestedConnectorSet.has(normalized);
    });
  }, [hasExplicitSuggestions, prioritizedConnectors, suggestedConnectorSet, normalizeConnectorType]);

  const prefetchConnectorTypes = useMemo(() => {
    const uniqueTypes = new Set();
    visibleConnectors.forEach((connector) => {
      const connectorType = String(
        connector?.props?.type || (normalizeConnectorType(connector?.title || '') === 'folders' ? 'folders' : '')
      ).trim();
      if (connectorType) {
        uniqueTypes.add(connectorType);
      }
    });
    return Array.from(uniqueTypes);
  }, [visibleConnectors, normalizeConnectorType]);
  const prefetchConnectorTypesKey = useMemo(
    () => prefetchConnectorTypes.join('|'),
    [prefetchConnectorTypes]
  );

  useEffect(() => {
    if (typeof sendMessage !== 'function' || !caseid || prefetchConnectorTypes.length === 0) {
      return;
    }
    sendMessage({
      command: 'listAllGenericDatasources',
      caseid,
      types: prefetchConnectorTypes,
    });
  }, [sendMessage, caseid, prefetchConnectorTypesKey]);

  useEffect(() => {
    if (typeof sendMessage !== 'function' || !caseid) {
      return;
    }
    sendMessage({
      command: 'getDatasourceSuggestions',
      caseid,
    });
  }, [sendMessage, caseid]);

  const matchesSearch = useCallback((connector) => (
    String(connector?.title || '').toLowerCase().includes(searchTerm.toLowerCase())
  ), [searchTerm]);

  const filteredMainConnectors = useMemo(() => {
    if (hasExplicitSuggestions && suggestedConnectors.length > 0) {
      return suggestedConnectors;
    }
    return prioritizedConnectors.filter(matchesSearch);
  }, [hasExplicitSuggestions, suggestedConnectors, prioritizedConnectors, matchesSearch]);

  const filteredOtherConnectors = useMemo(() => {
    if (!(hasExplicitSuggestions && suggestedConnectors.length > 0)) {
      return [];
    }
    return otherConnectors.filter(matchesSearch);
  }, [hasExplicitSuggestions, suggestedConnectors, otherConnectors, matchesSearch]);

  const showSuggestedSection = hasExplicitSuggestions && suggestedConnectors.length > 0;
  const showOtherSection = showSuggestedSection && otherConnectors.length > 0;
  const isOtherSectionOpen = showOtherSection && (otherSectionExpanded || Boolean(searchTerm.trim()));
  const showGlobalNoResults = !showSuggestedSection && filteredMainConnectors.length === 0 && Boolean(searchTerm.trim());

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const renderDatasourceControls = useCallback(() => (
    <Box sx={{
      mb: { xs: 2, sm: 3 },
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          width: '100%',
          maxWidth: 620,
          mx: 'auto',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: { xs: '100%', sm: 320 },
            width: '100%',
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            border: '2px solid rgba(87, 87, 176, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'rgba(87, 87, 176, 0.3)',
              boxShadow: '0 4px 20px rgba(87, 87, 176, 0.15)',
            },
            '&:focus-within': {
              borderColor: '#5757b0',
              boxShadow: '0 0 0 4px rgba(87, 87, 176, 0.1)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <SearchIcon sx={{ color: '#5757b0', ml: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.3rem' } }} />
          <TextField
            fullWidth
            variant="standard"
            placeholder={t("search.placeholder", "Search connectors...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              mx: { xs: 0.5, sm: 1 },
              '& .MuiInputBase-root': {
                padding: { xs: '4px 8px', sm: '8px 8px' },
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                fontWeight: '500',
                color: '#333',
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(87, 87, 176, 0.6)',
                opacity: 1
              },
              '& .MuiInput-underline:before': { borderBottom: 'none' },
              '& .MuiInput-underline:after': { borderBottom: 'none' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
            }}
            InputProps={{
              disableUnderline: true,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    sx={{
                      mr: 1,
                      color: 'rgba(87, 87, 176, 0.6)',
                      '&:hover': {
                        color: '#5757b0',
                        backgroundColor: 'rgba(87, 87, 176, 0.08)'
                      }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setIsCreatorDialogOpen(true)}
          sx={{
            position: 'relative',
            minWidth: { xs: '100%', sm: 'auto' },
            px: 2.5,
            py: 1.2,
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 700,
            color: '#5757b0',
            borderColor: 'rgba(87, 87, 176, 0.25)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,249,255,1) 100%)',
            boxShadow: '0 8px 24px rgba(87, 87, 176, 0.12)',
            '&:hover': {
              borderColor: '#5757b0',
              background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(240,243,255,1) 100%)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {t('connector_creator.button')}
            </Typography>
            <Chip
              size="small"
              color="warning"
              label={t('workflow.add_action_menu.labels.beta', 'BETA')}
              sx={{
                fontWeight: 700,
                fontSize: '0.6rem',
                height: 20,
                pointerEvents: 'none',
                borderRadius: '999px',
              }}
            />
          </Box>
        </Button>
      </Box>
    </Box>
  ), [handleClearSearch, searchTerm, t]);

  const renderConnectorGrid = useCallback((connectors, sectionKey) => (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="center"
      alignItems="stretch"
      gap={0}
      flexWrap="wrap"
      sx={{
        width: '100%',
        minHeight: connectors.length > 0 ? 'auto' : '120px',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '& > *': {
          display: 'flex',
          alignItems: 'stretch',
        }
      }}
    >
      <TransitionGroup component={null}>
        {connectors.map((connector, index) => {
          const { Component, props, title } = connector;
          const normalizedType = normalizeConnectorType(props?.type || title || '');
          const profileState = connectorProfiles[normalizedType] || {};
          const baseProps = Component === GenericConnector
            ? {
              ...props,
              skipInitialDatasourceLoad: true,
              hasDatasourceProfileHint: Boolean(profileState.hasProfile),
              hasDatasourceEnabledHint: Boolean(profileState.hasEnabledProfile),
            }
            : props;
          const targetConnectorToOpen = createdConnectorToOpen || autoOpenConnector;
          const shouldAutoOpen = targetConnectorToOpen && title === targetConnectorToOpen;
          const componentProps = shouldAutoOpen
            ? {
              ...baseProps,
              forceOpen: true,
              onOpen: () => {
                if (createdConnectorToOpen) {
                  setCreatedConnectorToOpen('');
                }
                if (typeof onConnectorOpened === 'function') {
                  onConnectorOpened();
                }
              }
            }
            : baseProps;

          const stableConnectorKey = normalizedType
            || String(props?.type || title || Component?.name || 'connector');

          return (
            <Grow
              key={`${sectionKey}-${stableConnectorKey}`}
              in={isVisible}
              timeout={{
                enter: 500 + (index * 100),
                exit: 300,
              }}
              style={{
                transformOrigin: '0 0',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  width: { xs: '90px', sm: '110px', md: '130px' },
                  minHeight: { xs: '100px', sm: '110px', md: '130px' },
                  animation: `slideInUp 0.7s ease-out ${index * 0.08}s both`,
                  '@keyframes slideInUp': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(30px) scale(0.8)',
                    },
                    '60%': {
                      opacity: 0.8,
                      transform: 'translateY(-3px) scale(1.05)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0px) scale(1)',
                    },
                  },
                  '& > *': {
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }
                }}
              >
                <Component {...componentProps} />
              </Box>
            </Grow>
          );
        })}
      </TransitionGroup>
    </Box>
  ), [
    autoOpenConnector,
    connectorProfiles,
    createdConnectorToOpen,
    isVisible,
    normalizeConnectorType,
    onConnectorOpened
  ]);

  // Description text and tooltip (ensure visible in compact/mobile views)
  const descriptionText = t("dataconnectors.description", "Use these connectors to bring emails, files, and messages into this case so everything stays in one place. Pick a connector (for example: Gmail or Dropbox) and follow the simple on-screen steps.");
  const tooltipTitle = (isMobile || hideHeader)
    ? `${t("settings.manage_data_connectors")} — ${descriptionText}`
    : t("settings.manage_data_connectors");

  if (isReadOnly) {
    return null;
  }

  return (
    <>
      {!hideHeader && (
        <Fade in={true} timeout={600}>
          <Box sx={{ mb: { xs: 1, sm: 2 } }}>
            <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
              {t("dataconnectors.title")}
              <Tooltip title={tooltipTitle}>
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  aria-label={t("dataconnectors.title") + " info"}
                >
                  <InfoIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="datasources-description"
              sx={{ maxWidth: { xs: '100%', sm: '70%' }, mt: 1 }}
            >
              {descriptionText}
            </Typography>
          </Box>
        </Fade>
      )}

      {!showSuggestedSection && (
        <Fade in={isVisible} timeout={600}>
          {renderDatasourceControls()}
        </Fade>
      )}

      {/* Compact / embedded view: show description under the search when header is hidden */}
      {hideHeader && (
        <Box sx={{ width: '100%', textAlign: 'center', mt: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="datasources-description-compact"
            sx={{ mx: 'auto', maxWidth: 640 }}
          >
            {descriptionText}
          </Typography>
        </Box>
      )}

      <Fade in={isVisible} timeout={800}>
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'transparent' }}>
          {showPluginRuntimeWarning && (
            <Alert
              severity="warning"
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {t(
                'dataconnectors.plugin_runtime_disabled',
                'Containerized plugin connectors require the plugin runtime, but plugin_runtime.enabled is currently disabled. Enable plugin_runtime.enabled (and configure plugin_runtime.image if needed) to use them.'
              )}
            </Alert>
          )}
          {showGlobalNoResults ? (
            <Fade in={true} timeout={500}>
              <Box
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body1">
                  {t("search.no_results", "No connectors found matching your search.")}
                </Typography>
              </Box>
            </Fade>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {showSuggestedSection && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        px: 2.25,
                        py: 1,
                        borderRadius: '999px',
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(241,244,255,1) 100%)',
                        border: '1px solid rgba(87, 87, 176, 0.14)',
                        boxShadow: '0 10px 28px rgba(87, 87, 176, 0.10)',
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: '1rem', color: '#5757b0' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: '#5757b0',
                          letterSpacing: '0.01em',
                        }}
                      >
                        {t('dataconnectors.suggested_section', 'Suggested connectors')}
                      </Typography>
                    </Box>
                  </Box>
                  {renderConnectorGrid(filteredMainConnectors, 'suggested')}
                </Box>
              )}

              {!showSuggestedSection && renderConnectorGrid(filteredMainConnectors, 'all')}

              {showOtherSection && (
                <Accordion
                  disableGutters
                  expanded={isOtherSectionOpen}
                  onChange={(_event, expanded) => setOtherSectionExpanded(expanded)}
                  elevation={0}
                  sx={{
                    background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(87, 87, 176, 0.12)',
                    borderRadius: '18px !important',
                    overflow: 'hidden',
                    '&:before': {
                      display: 'none',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#5757b0' }} />}
                    sx={{
                      px: 2,
                      py: 0.5,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(246,247,255,0.95) 100%)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, color: '#5757b0' }}>
                        {t('dataconnectors.other_section', 'Other datasources')}
                      </Typography>
                      <Chip
                        size="small"
                        label={filteredOtherConnectors.length}
                        sx={{ fontWeight: 700, backgroundColor: 'rgba(87, 87, 176, 0.08)', color: '#5757b0' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 1.5 }}>
                    {renderDatasourceControls()}
                    {filteredOtherConnectors.length === 0 && searchTerm.trim() ? (
                      <Box
                        sx={{
                          width: '100%',
                          textAlign: 'center',
                          py: 2,
                          color: 'text.secondary',
                        }}
                      >
                        <Typography variant="body2">
                          {t("search.no_results", "No connectors found matching your search.")}
                        </Typography>
                      </Box>
                    ) : (
                      renderConnectorGrid(filteredOtherConnectors, 'other')
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </Paper>
      </Fade>
      <ConnectorCreatorDialog
        open={isCreatorDialogOpen}
        onClose={() => setIsCreatorDialogOpen(false)}
        defaultCreatorType="connector"
        lockCreatorType={true}
        onPluginCreated={(payload) => {
          const planKind = String(payload?.created?.manifest?.kind || '').toLowerCase();
          if (planKind === 'connector' && typeof sendMessage === 'function') {
            const connectorName = String(
              payload?.created?.manifest?.name
              || payload?.plan?.plugin_name
              || ''
            ).trim();
            if (connectorName) {
              setCreatedConnectorToOpen(connectorName);
            }
            sendMessage({
              command: 'get_server_info',
              data: { force_reload: true },
            });
          }
        }}
      />
    </>
  );
};

export default SettingDatasources;
